import type { Segment } from "../types"

/** How a company relates to a segment. */
export type ConnectionRole = "customer" | "competitor"

/** The single badge/fill role a company displays when its per-segment roles are
 * aggregated — matches the map legend (Customer / Competitor / Both). */
export type CompanyDisplayRole = "customer" | "competitor" | "both"

export function companyDisplayRole(roles: ConnectionRole[]): CompanyDisplayRole {
  const isCustomer = roles.includes("customer")
  const isCompetitor = roles.includes("competitor")
  if (isCustomer && isCompetitor) return "both"
  return isCustomer ? "customer" : "competitor"
}

/** Company bubbles/badges use role-coloured fills straight from the design
 * tokens (customer = primary, competitor = muted) — the single source the map,
 * its legend, and the related-companies grouping all fill from (#3306 #15). */
export const COMPANY_FILL: Record<CompanyDisplayRole, string> = {
  customer: "hsl(var(--primary))",
  competitor: "hsl(var(--muted-foreground))",
  both: "hsl(var(--warning))",
}

/** Display order + label for the aggregated role, matching the map legend
 * (Customer / Competitor / Both) — shared with the related-companies
 * grouping (#3306 #15). */
export const COMPANY_DISPLAY_ROLE_ORDER: Array<{ role: CompanyDisplayRole; label: string }> = [
  { role: "customer", label: "Customer" },
  { role: "competitor", label: "Competitor" },
  { role: "both", label: "Both" },
]

/** One company aggregated across every segment it appears in (customer or competitor). */
export type CompanyDirectoryEntry = {
  name: string
  roles: ConnectionRole[]
  segments: Array<{ id: string; name: string; marketName: string }>
}

/**
 * Bucket a list of companies into Customer / Competitor / Both, using the same
 * role resolution as the map bubbles/legend. Empty buckets are omitted so
 * callers can render each group's header only when it has entries (#3306 #15).
 */
export function groupCompaniesByDisplayRole<T extends { roles: ConnectionRole[] }>(
  entries: T[]
): Array<{ role: CompanyDisplayRole; label: string; entries: T[] }> {
  return COMPANY_DISPLAY_ROLE_ORDER.map(({ role, label }) => ({
    role,
    label,
    entries: entries.filter((entry) => companyDisplayRole(entry.roles) === role),
  })).filter((group) => group.entries.length > 0)
}

/** Framework-agnostic graph model (the React Flow component maps + lays these out). */
export type ConnectionsNode =
  | { id: string; kind: "market"; label: string; color: string }
  | {
      id: string
      kind: "segment"
      label: string
      segmentId: string
      marketName: string
      sales: number
    }
  | { id: string; kind: "company"; label: string; roles: ConnectionRole[] }

export type ConnectionsEdge = {
  id: string
  source: string
  target: string
  role?: ConnectionRole
}

export type ConnectionsGraphData = {
  nodes: ConnectionsNode[]
  edges: ConnectionsEdge[]
}

const companyKey = (name: string) => name.trim().toLowerCase()

export const marketNodeId = (market: string) => `market:${companyKey(market)}`
export const segmentNodeId = (segmentId: string) => `segment:${segmentId}`
export const companyNodeId = (name: string) => `company:${companyKey(name)}`

function rolePairs(segment: Segment): Array<{ name: string; role: ConnectionRole }> {
  return [
    ...segment.customers.map((name) => ({ name, role: "customer" as const })),
    ...segment.competitors.map((name) => ({ name, role: "competitor" as const })),
  ]
}

/**
 * Aggregate every customer/competitor named across the portfolio into a deduped
 * directory keyed case-insensitively by company name. Powers the Companies index
 * and the dossier's "related companies" list — no backend call needed, since the
 * portfolio payload already carries each segment's customers/competitors.
 */
export function buildCompanyDirectory(segments: Segment[]): CompanyDirectoryEntry[] {
  const byKey = new Map<
    string,
    {
      name: string
      roles: Set<ConnectionRole>
      segments: Map<string, CompanyDirectoryEntry["segments"][number]>
    }
  >()

  for (const segment of segments) {
    for (const { name, role } of rolePairs(segment)) {
      const trimmed = name.trim()
      if (!trimmed) continue
      const key = companyKey(trimmed)
      const entry = byKey.get(key) ?? {
        name: trimmed,
        roles: new Set<ConnectionRole>(),
        segments: new Map(),
      }
      entry.roles.add(role)
      entry.segments.set(segment.id, {
        id: segment.id,
        name: segment.name,
        marketName: segment.marketName,
      })
      byKey.set(key, entry)
    }
  }

  return Array.from(byKey.values())
    .map((entry) => ({
      name: entry.name,
      roles: Array.from(entry.roles).sort(),
      segments: Array.from(entry.segments.values()),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Companies sharing at least one segment with `name`, excluding `name` itself. */
export function relatedCompanies(
  directory: CompanyDirectoryEntry[],
  name: string
): CompanyDirectoryEntry[] {
  const target = directory.find((entry) => companyKey(entry.name) === companyKey(name))
  if (!target) return []
  const segmentIds = new Set(target.segments.map((segment) => segment.id))
  return directory.filter(
    (entry) =>
      companyKey(entry.name) !== companyKey(name) &&
      entry.segments.some((segment) => segmentIds.has(segment.id))
  )
}

/**
 * Build a Markets → Segments → Companies graph from the portfolio. Node ids are
 * namespaced so the renderer can tell kinds apart and wire click-through.
 */
export function buildConnectionsGraph(
  segments: Segment[],
  marketColors: Record<string, string>,
  fallbackColor = "#94A3B8"
): ConnectionsGraphData {
  const nodes = new Map<string, ConnectionsNode>()
  const edges = new Map<string, ConnectionsEdge>()

  for (const segment of segments) {
    const marketId = marketNodeId(segment.marketName)
    if (!nodes.has(marketId)) {
      nodes.set(marketId, {
        id: marketId,
        kind: "market",
        label: segment.marketName,
        color: marketColors[segment.marketName] ?? fallbackColor,
      })
    }

    const segId = segmentNodeId(segment.id)
    nodes.set(segId, {
      id: segId,
      kind: "segment",
      label: segment.name,
      segmentId: segment.id,
      marketName: segment.marketName,
      sales: segment.sales,
    })
    const marketEdgeId = `${marketId}->${segId}`
    edges.set(marketEdgeId, { id: marketEdgeId, source: marketId, target: segId })

    for (const { name, role } of rolePairs(segment)) {
      const trimmed = name.trim()
      if (!trimmed) continue
      const compId = companyNodeId(trimmed)
      const existing = nodes.get(compId)
      if (existing && existing.kind === "company") {
        if (!existing.roles.includes(role)) existing.roles.push(role)
      } else {
        nodes.set(compId, { id: compId, kind: "company", label: trimmed, roles: [role] })
      }
      const edgeId = `${segId}->${compId}`
      // Keep the first role seen for the edge; a company can be a customer in one
      // segment and a competitor in another, but each edge is segment-specific.
      if (!edges.has(edgeId)) {
        edges.set(edgeId, { id: edgeId, source: segId, target: compId, role })
      }
    }
  }

  return { nodes: Array.from(nodes.values()), edges: Array.from(edges.values()) }
}
